// src/hooks/useAttendance.js
import { useState, useEffect, useCallback, useRef } from 'react'
import { getAttendance, upsertAttendance, deleteAttendance, getScheduleHistory } from '../db/index.js'
import { logEvent, getCurrentAdmin } from '../lib/agentLog.js'
import { tLog } from '../lib/i18n.js'
import { fmtDuration, fmtDelta, diffMinutes, scheduledMinutes } from '../lib/attendance.js'

export function useAttendance(monthStr, agents = []) {
  const [rows,    setRows]    = useState([])
  const [scheduleHistory, setScheduleHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)

  // Keep agents in a ref so callbacks always see the latest value
  // without needing to be in the dependency array
  const agentsRef = useRef(agents)
  useEffect(() => { agentsRef.current = agents }, [agents])

  const [y, m] = monthStr.split('-').map(Number)
  const prevM  = m === 1  ? `${y-1}-12`          : `${y}-${String(m-1).padStart(2,'0')}`
  const nextM  = m === 12 ? `${y+1}-01`          : `${y}-${String(m+1).padStart(2,'0')}`
  const dateFrom = `${prevM}-01`
  const dateTo   = `${nextM}-${new Date(Number(nextM.split('-')[0]), Number(nextM.split('-')[1]), 0).getDate()}`

  // ⚠️ GENERATION-GUARDED (attendance audit 2026-10-21): a slow answer for the PREVIOUS window could land after the new
  // one and replace it, under the new month's heading. Only the latest request may commit.
  // The roster is part of the key too: schedule history is read per agent, and a roster change inside the same month
  // used to keep the old agents' history until the month changed.
  const genRef = useRef(0)
  const rosterKey = agents.map(a => a.id).sort().join(',')
  const load = useCallback(async () => {
    const gen = ++genRef.current
    setLoading(true)
    try {
      const agentIds = agentsRef.current.map(a => a.id)
      const [data, hist] = await Promise.all([
        getAttendance({ dateFrom, dateTo }),
        getScheduleHistory(agentIds),
      ])
      if (gen !== genRef.current) return
      setRows(data)
      setScheduleHistory(hist)
      setError(null)
    } catch (e) {
      if (gen === genRef.current) setError(e.message || 'error')
    } finally {
      if (gen === genRef.current) setLoading(false)
    }
  }, [dateFrom, dateTo, rosterKey])

  useEffect(() => { load() }, [load])

  // Keep rows in a ref too for the remove callback
  const rowsRef = useRef(rows)
  useEffect(() => { rowsRef.current = rows }, [rows])

  const byAgent = {}
  rows.forEach(r => {
    if (!byAgent[r.agent_id]) byAgent[r.agent_id] = {}
    byAgent[r.agent_id][r.date] = r
  })

  const upsert = useCallback(async (record, prevRecord) => {
    const saved = await upsertAttendance(record)
    setRows(prev => {
      const filtered = prev.filter(r => !(r.agent_id === record.agent_id && r.date === record.date))
      return [...filtered, saved]
    })

    // Use ref so we always have the latest agents list
    const agent = agentsRef.current.find(a => a.id === record.agent_id)
    if (agent) {
      const by          = getCurrentAdmin()
      const displayDate = new Date(record.date + 'T12:00:00')
        .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
      const schedMins   = scheduledMinutes(agent, record.date)

      // Diff vs previous record
      const changes = []
      if (!prevRecord) {
        changes.push(tLog('log_diff_checkin', { from: '—', to: record.check_in || '—' }))
      } else {
        if (prevRecord.status !== record.status)
          changes.push(tLog('log_diff_field', { label: 'Status', from: prevRecord.status, to: record.status }))
        if ((prevRecord.check_in || '') !== (record.check_in || ''))
          changes.push(tLog('log_diff_checkin', { from: prevRecord.check_in || '—', to: record.check_in || '—' }))
        if ((prevRecord.check_out || '') !== (record.check_out || ''))
          changes.push(tLog('log_diff_checkout', { from: prevRecord.check_out || '—', to: record.check_out || '—' }))
        if ((prevRecord.note || '') !== (record.note || '') && record.note)
          changes.push(tLog('log_diff_note', { note: record.note }))
      }

      // Nothing changed — skip logging
      if (changes.length > 0) {

      let category = 'attendance'
      let msg = tLog('log_att_entry', { date: displayDate, changes: changes.join('; '), by })

      if (record.status === 'absent') {
        category = 'alert'
        msg = tLog('log_att_absent', { date: displayDate, by, note: record.note ? tLog('log_diff_note', { note: record.note }) : '' })
      } else if (record.status === 'holiday') {
        msg = tLog('log_att_holiday', { date: displayDate, by })
      } else if (Array.isArray(record.slots) && record.slots.length > 0) {
        // Per-slot model
        const worked = record.slots.reduce((sum, s) => {
          if (s.status) return sum
          const m = diffMinutes(s.check_in, s.check_out)
          return sum + (m ?? 0)
        }, 0)
        const delta = worked > 0 && schedMins > 0 ? worked - schedMins : null
        if (worked > 0) msg += tLog('log_att_worked', { duration: fmtDuration(worked) })
        if (delta != null && Math.abs(delta) > 10) {
          msg += tLog('log_att_delta', { delta: fmtDelta(delta) })
          if (delta < -30) category = 'alert'
        }
      } else if (record.check_in && record.check_out) {
        const worked = diffMinutes(record.check_in, record.check_out)
        const delta  = worked != null && schedMins > 0 ? worked - schedMins : null
        if (worked != null) msg += tLog('log_att_worked', { duration: fmtDuration(worked) })
        if (delta != null && Math.abs(delta) > 10) {
          msg += tLog('log_att_delta', { delta: fmtDelta(delta) })
          if (delta < -30) category = 'alert'
        }
      }

      logEvent(agent.id, agent.name, category, msg)
      } // end if changes.length > 0
    }

    return saved
  }, []) // stable — uses refs internally

  const remove = useCallback(async (id) => {
    const rec   = rowsRef.current.find(r => r.id === id)
    const agent = rec && agentsRef.current.find(a => a.id === rec.agent_id)
    if (agent && rec) {
      logEvent(agent.id, agent.name, 'attendance',
        tLog('log_att_deleted', { date: rec.date, by: getCurrentAdmin() }))
    }
    await deleteAttendance(id)
    setRows(prev => prev.filter(r => r.id !== id))
  }, []) // stable — uses refs internally

  return { rows, byAgent, scheduleHistory, loading, error, reload: load, upsert, remove }
}
